'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n, type TranslationKey } from '@/i18n';
import { useInstanceConfig } from '@/config/InstanceConfigProvider';
import {
    ArrowUp,
    ArrowDown,
    ZapFast,
    CheckDone01,
    Grid02,
    UsersCheck,
} from '@untitledui/icons';
import { AppLayoutExpanded } from '@/components/layouts/app-layout-expanded';
import { Button } from '@/components/base/buttons/button';
import { FeaturedIcon } from '@/components/foundations/featured-icon/featured-icon';
import { Avatar } from '@/components/base/avatar/avatar';
import { AvatarAddButton } from '@/components/base/avatar/base-components/avatar-add-button';
import { dashboardService, kbStatsByCategory, type DashboardStats } from '@/services/dashboard-service';
import { teamManagementService, type TeamMember } from '@/services/team-management-service';
import { cx } from '@/utils/cx';

// ─── Greeting ─────────────────────────────────────────────────────────────────

function getGreetingKey(): 'dashboard.greetingMorning' | 'dashboard.greetingAfternoon' | 'dashboard.greetingEvening' {
    const h = new Date().getHours();
    if (h < 12) return 'dashboard.greetingMorning';
    if (h < 17) return 'dashboard.greetingAfternoon';
    return 'dashboard.greetingEvening';
}

function titleCase(s: string): string {
    return s.replace(/\b\w/g, c => c.toUpperCase());
}

function getUserName(email?: string, displayName?: string): string {
    if (displayName) return titleCase(displayName);
    if (email) return titleCase(email.split('@')[0].replace(/[._]/g, ' '));
    return '';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pctChange(current: number, prev: number): number {
    if (prev === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - prev) / prev) * 100);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// NOTE-043: the dashboard renders one KB stat card per category the active
// variant declares (instanceConfig.kbCategories), so counts/labels/links stay
// in lockstep with the variant instead of being branched per variant inline.
// `kbStats` is keyed by category id (global / funding / project / procurement).
const KB_STAT_LABEL_KEY: Record<string, TranslationKey> = {
    global: 'dashboard.kbGlobal',
    funding: 'dashboard.kbFunding',
    project: 'dashboard.kbProject',
    procurement: 'dashboard.kbProcurement',
};

// Static classes so Tailwind keeps them; keyed by number of KB categories.
const KB_GRID_COLS: Record<number, string> = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
};

function KbStatCard({
    label,
    stats,
    viewHref,
    createHref,
}: {
    label: string;
    stats: { count: number; prevMonthCount: number };
    viewHref: string;
    createHref: string;
}) {
    const router = useRouter();
    const { t } = useI18n();
    const delta = pctChange(stats.count, stats.prevMonthCount);
    const isUp = delta >= 0;

    return (
        <div className="flex flex-col gap-4 rounded-xl border border-secondary bg-gray-25 p-5 shadow-xs">
            <p className="text-sm font-semibold text-secondary">{label}</p>
            <div className="flex items-baseline gap-2">
                <span className="font-display text-display-md font-semibold text-primary">{stats.count}</span>
                <span className={cx('flex items-center gap-1 text-sm font-medium', isUp ? 'text-success-600' : 'text-error-600')}>
                    {isUp ? <ArrowUp className="size-4" /> : <ArrowDown className="size-4" />}
                    {Math.abs(delta)}{t('dashboard.kbVsLastMonth')}
                </span>
            </div>
            <div className="flex items-center justify-between">
                <button onClick={() => router.push(viewHref)} className="cursor-pointer text-sm font-semibold text-secondary hover:text-secondary_hover">
                    {t('dashboard.kbView')}
                </button>
                <button onClick={() => router.push(createHref)} className="cursor-pointer text-sm font-semibold text-brand-700 hover:text-brand-800">
                    {t('dashboard.kbCreate')}
                </button>
            </div>
        </div>
    );
}

function getInitials(nameOrEmail: string): string {
    // If it looks like an email, extract from prefix
    const raw = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
    const parts = raw.split(/[\s._-]/).filter(Boolean);
    return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].substring(0, 2).toUpperCase();
}

function UsersCard({ userCount, members }: { userCount: number; members: TeamMember[] }) {
    const router = useRouter();
    const { t } = useI18n();
    const displayMembers = members.slice(0, 4);

    return (
        <div className="flex flex-col rounded-xl border border-secondary bg-primary p-5 shadow-xs">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm font-semibold text-secondary">{t('dashboard.usersTitle')}</p>
                    <p className="text-xs text-tertiary">{t('dashboard.usersSubtitle')}</p>
                </div>
                <FeaturedIcon color="gray" size="md" theme="modern-neue">
                    <UsersCheck className="size-5" />
                </FeaturedIcon>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-display-md font-semibold text-primary">{userCount}</span>
                <span className="text-sm text-secondary">{t('dashboard.usersActive')}</span>
            </div>
            <div className="mt-auto flex items-center justify-between pt-4">
                <button onClick={() => router.push('/user-management?action=invite')} className="cursor-pointer text-sm font-semibold text-secondary hover:text-secondary_hover">
                    {t('dashboard.usersAdd')}
                </button>
                <div className="flex items-center gap-3">
                    <button onClick={() => router.push('/user-management')} className="cursor-pointer text-sm font-semibold text-brand-700 hover:text-brand-800">
                        {t('dashboard.usersManage')}
                    </button>
                    <div className="flex -space-x-2">
                        {displayMembers.map((m) => (
                            <Avatar key={m.userId} size="sm" src={m.photoUrl || undefined} initials={getInitials(m.displayName || m.email)} alt={m.displayName || m.email} className="ring-2 ring-white" />
                        ))}
                        <AvatarAddButton size="sm" title="Add user" className="ring-2 ring-white" onPress={() => router.push('/user-management?action=invite')} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function UsageGauge() {
    // SVG semi-circle gauge — centre at (100, 110), radius 85. Renders only the static
    // background arc; this is a "Coming soon" card, so there is no fill/percent to compute.
    const CX = 100;
    const CY = 110;
    const R = 85;

    function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
        const rad = (angleDeg * Math.PI) / 180;
        return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
    }

    // Arc from 180° (left) to 0° (right)
    const startDeg = 180;
    function describeArc(startD: number, endD: number) {
        const s = polarToCartesian(CX, CY, R, startD);
        const e = polarToCartesian(CX, CY, R, endD);
        const large = Math.abs(startD - endD) > 180 ? 1 : 0;
        return `M ${s.x} ${s.y} A ${R} ${R} 0 ${large} 1 ${e.x} ${e.y}`;
    }

    const { t } = useI18n();
    return (
        <div className="relative flex flex-col overflow-hidden rounded-xl border border-secondary bg-primary p-5 shadow-xs">
            <div className="mb-2 flex items-start justify-between">
                <p className="text-sm font-semibold text-secondary">{t('dashboard.usageTitle')}</p>
            </div>
            <div className="flex flex-1 flex-col items-center justify-center pointer-events-none select-none">
                <svg viewBox="0 0 200 120" className="w-48 overflow-visible">
                    <path d={describeArc(startDeg, 0)} fill="none" stroke="#f3f4f6" strokeWidth={14} strokeLinecap="round" />
                </svg>
            </div>
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center rounded-xl bg-gray-100/90">
                <p className="font-sans text-lg font-semibold text-gray-600">{t('dashboard.comingSoon')}</p>
            </div>
        </div>
    );
}

function FeatureCard({
    title,
    description,
    actionLabel,
    actionHref,
    icon: Icon,
    disabled,
}: {
    title: string;
    description: string;
    actionLabel: string;
    actionHref: string;
    icon: React.FC<{ className?: string }>;
    disabled?: boolean;
}) {
    const router = useRouter();
    const { t } = useI18n();
    return (
        <div className="relative flex flex-col gap-1 rounded-xl border border-secondary bg-primary p-5 shadow-xs">
            <FeaturedIcon color="gray" size="md" theme="modern-neue" className="absolute right-5 top-5">
                <Icon className="size-5" />
            </FeaturedIcon>
            <p className="pr-12 text-sm font-semibold text-secondary">{title}</p>
            <p className="pr-12 text-sm text-tertiary">{description}</p>
            {disabled ? (
                <span className="mt-1 self-start text-sm text-quaternary">{t('dashboard.comingSoon')}</span>
            ) : (
                <button
                    onClick={() => router.push(actionHref)}
                    className="mt-1 self-start text-sm font-semibold text-brand-700 hover:text-brand-800"
                >
                    {actionLabel}
                </button>
            )}
        </div>
    );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────────

export function DashboardPage() {
    const { user, displayName: authDisplayName } = useAuth();
    const isAdmin = user?.groups?.includes('admin') || false;
    const { t } = useI18n();
    const instanceConfig = useInstanceConfig();
    const isFunding = instanceConfig.variant === 'funding';
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

    useEffect(() => {
        dashboardService.getStats()
            .then(setStats)
            .catch((err) => console.error('[DashboardPage] Failed to load stats:', err));
        teamManagementService.getTeamMembers()
            .then(setTeamMembers)
            .catch(() => {});
    }, []);

    const displayName = getUserName(user?.email, authDisplayName || undefined);

    // Indexed by KB category id so the stat cards can be rendered from the variant config.
    const kbCategoryStats = kbStatsByCategory(stats?.kbStats);

    return (
        <AppLayoutExpanded activeUrl="/">
            <div className="flex-1 overflow-auto px-6 py-8 lg:px-8">
                {/* Greeting */}
                <div className="mb-6">
                    <h1 className="font-display text-display-sm font-semibold text-primary">
                        {isFunding
                            ? `${t('dashboard.fundingGreeting')}${displayName ? ` ${displayName}` : ''}`
                            : `${t(getGreetingKey())}${displayName ? `, ${displayName}` : ''}`}
                    </h1>
                    <p className="mt-1 text-sm text-tertiary">
                        {isFunding
                            ? t('dashboard.fundingTagline')
                            : t('dashboard.tagline')}
                    </p>
                </div>

                {/* Analytics card — static "Coming soon" placeholder. The full recharts chart +
                    date/series filters used to render only to sit behind a permanent overlay, so
                    they were removed (dead JS, wasted renders) until analytics actually ships. */}
                <div className="mb-5 flex min-h-56 flex-col items-center justify-center rounded-xl border border-secondary bg-secondary/30 p-5 text-center shadow-xs">
                    <p className="text-md font-semibold text-primary">{t('dashboard.analyticsTitle')}</p>
                    <p className="mt-2 font-sans text-lg font-semibold text-gray-600">{t('dashboard.comingSoon')}</p>
                    <p className="mt-1 font-sans text-sm text-gray-500">{t('dashboard.analyticsComingSoonSubtitle')}</p>
                </div>

                {/* KB Stats row — one card per KB category (admin only: Operators have no KB
                    access, the sidebar hides KB from them, and View/Create lead to admin pages). */}
                {isAdmin && (
                    <div className={cx('mb-5 grid grid-cols-1 gap-4', KB_GRID_COLS[instanceConfig.kbCategories.length] ?? 'sm:grid-cols-3')}>
                        {instanceConfig.kbCategories.map((cat) => (
                            <KbStatCard
                                key={cat.id}
                                label={t(KB_STAT_LABEL_KEY[cat.id] ?? 'dashboard.kbGlobal')}
                                stats={kbCategoryStats[cat.id] ?? { count: 0, prevMonthCount: 0 }}
                                viewHref={cat.url}
                                createHref={`${cat.url}/create-knowledgebase`}
                            />
                        ))}
                    </div>
                )}

                {/* Users + Usage row (admin only) */}
                {isAdmin && (
                    <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <UsersCard userCount={stats?.userCount ?? 0} members={teamMembers} />
                        <UsageGauge />
                    </div>
                )}

                {/* Feature cards row */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {isFunding ? (
                        <>
                            <FeatureCard
                                title={t('funding.featureAssessTitle')}
                                description={t('funding.featureAssessDescription')}
                                actionLabel={t('funding.featureAssessAction')}
                                actionHref="/assess-applications"
                                icon={CheckDone01}
                            />
                            <FeatureCard
                                title={t('funding.featureAnalyticsTitle')}
                                description={t('funding.featureAnalyticsDescription')}
                                actionLabel={t('funding.featureAnalyticsAction')}
                                actionHref="/analytics"
                                icon={ZapFast}
                                disabled
                            />
                        </>
                    ) : (
                        <>
                            <FeatureCard
                                title={t('dashboard.featureCreateTitle')}
                                description={t('dashboard.featureCreateDescription')}
                                actionLabel={t('dashboard.featureCreateAction')}
                                actionHref="/validate-documents"
                                icon={ZapFast}
                                disabled
                            />
                            <FeatureCard
                                title={t('dashboard.featureValidateTitle')}
                                description={t('dashboard.featureValidateDescription')}
                                actionLabel={t('dashboard.featureValidateAction')}
                                actionHref="/validate-documents"
                                icon={CheckDone01}
                            />
                        </>
                    )}
                </div>
            </div>
        </AppLayoutExpanded>
    );
}
